import { useState } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useItems, type Item } from "@/api/items";
import { BulkActionsBar } from "@/components/data-table/BulkActionsBar";
import { DataTable } from "@/components/data-table/DataTable";
import { useDataTableState } from "@/components/data-table/useDataTableState";
import {
  itemLabelEntity,
  planningPolicyEntity,
} from "@/components/entity-select/entities";
import { EntityFilter } from "@/components/entity-select/EntityFilter";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

import { AssignPolicyDialog } from "./AssignPolicyDialog";
import { BulkLabelsDialog } from "./BulkLabelsDialog";
import { useItemsColumns } from "./items-columns";

export default function ItemsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [filterTags, setFilterTags] = useState<number[]>([]);
  // `?policy=<id>` deep-links a policy's members (e.g. from the blocked-delete dialog).
  const [policyFilter, setPolicyFilter] = useState<number[]>(() => {
    const p = searchParams.get("policy");
    return p && /^\d+$/.test(p) ? [Number(p)] : [];
  });
  const [withoutPolicyOnly, setWithoutPolicyOnly] = useState(false);

  const ds = useDataTableState({
    defaultSort: { id: "sku", desc: false },
    filterDeps: [filterTags, policyFilter, withoutPolicyOnly],
  });

  const items = useItems({
    search: ds.debouncedSearch || undefined,
    ordering: ds.orderingParam,
    labels: filterTags.length > 0 ? filterTags : undefined,
    planningPolicy: policyFilter.length ? policyFilter : undefined,
    withoutPolicy: withoutPolicyOnly || undefined,
    limit: ds.limit,
    offset: ds.offset,
  });

  const filtersActive =
    filterTags.length > 0 || policyFilter.length > 0 || withoutPolicyOnly;
  const resetFilters = () => {
    setFilterTags([]);
    setPolicyFilter([]);
    setWithoutPolicyOnly(false);
  };

  const columns = useItemsColumns(t);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);

  const selectedItems = (items.data?.results ?? []).filter((i) =>
    ds.selectedIds.includes(String(i.id)),
  );

  const openItem = (id: number, e: React.MouseEvent) => {
    const url = `/items/${id}/`;
    if (e.metaKey || e.ctrlKey) {
      window.open(url, "_blank")?.focus();
    } else {
      navigate(url);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title={t("items.title")} />

      <DataTable<Item>
        {...ds.tableProps}
        data={items.data?.results ?? []}
        totalCount={items.data?.count ?? 0}
        isLoading={items.isLoading}
        columns={columns}
        searchPlaceholder={t("items.searchPlaceholder")}
        emptyLabel={t("items.empty")}
        getRowId={(r) => String(r.id)}
        filterSlot={
          <>
            <EntityFilter
              title={t("items.filterTags")}
              descriptor={itemLabelEntity}
              value={filterTags}
              onChange={setFilterTags}
            />
            <EntityFilter
              title={t("items.filterPolicy")}
              descriptor={planningPolicyEntity}
              value={policyFilter}
              onChange={setPolicyFilter}
            />
            <Label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={withoutPolicyOnly}
                onCheckedChange={(v) => setWithoutPolicyOnly(!!v)}
              />
              {t("items.filterWithoutPolicy")}
            </Label>
          </>
        }
        onResetFilters={filtersActive ? resetFilters : undefined}
        bulkActionsSlot={
          ds.selectedIds.length > 0 ? (
            <BulkActionsBar
              count={ds.selectedIds.length}
              onClear={ds.clearSelection}
            >
              <Button size="sm" onClick={() => setBulkOpen(true)}>
                {t("items.editTags")}
              </Button>
              <Button size="sm" onClick={() => setAssignOpen(true)}>
                {t("items.assignPolicy")}
              </Button>
            </BulkActionsBar>
          ) : null
        }
        onRowClick={(row, e) => openItem(row.id, e)}
      />

      {bulkOpen && (
        <BulkLabelsDialog
          items={selectedItems}
          onClose={() => {
            setBulkOpen(false);
            ds.clearSelection();
          }}
        />
      )}
      {assignOpen && (
        <AssignPolicyDialog
          items={selectedItems}
          onClose={() => {
            setAssignOpen(false);
            ds.clearSelection();
          }}
        />
      )}
    </div>
  );
}
